// client/src/components/GoogleLoginButton.jsx
//
// ============================ GOOGLE AUTH ============================
// "Sign in with Google" using the authorization-code flow (@react-oauth/google).
//
// This whole FILE is the Google sign-in feature. To disable the feature you
// don't need to touch this file — just remove the <GoogleLoginButton /> usage
// (and the <GoogleOAuthProvider> wrapper in main.jsx). Both are marked with
// GOOGLE AUTH comment blocks.
//
// Website flow: Google popup → one-time `code` → POST /api/auth/google → the
// backend exchanges it, checks the company domain, looks the user up in 3CX
// and sets our normal session cookies.
//
// Desktop app flow (1.2.0+): Google refuses sign-in inside app windows, so the
// button asks the desktop app to open /desktop-login in the user's normal
// browser (see pages/DesktopLogin.jsx). When the browser hands back, the app
// gives us { handoff, verifier } and we redeem it at /api/auth/desktop/exchange,
// which sets the session cookies in the desktop app.
//
// Either way the resulting user goes back up via onSuccess, so <Login /> and the
// desktop footer finish exactly like a password login.
// ====================================================================

import { useCallback, useEffect, useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { desktopGoogleSignIn } from '../utils/desktopGoogleSignIn';
import './GoogleLoginButton.css';

const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:7080/api' : '/api';

async function postJson(url, body) {
    const res = await fetch(url, {
        method: 'POST',
        credentials: 'include', // backend sets the httpOnly session cookies
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
    });
    const data = await res.json().catch(() => ({}));
    return { res, data };
}

function GoogleIcon() {
    return (
        <svg className="btn-google-icon" viewBox="0 0 18 18" aria-hidden="true">
            <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.71-1.57 2.68-3.89 2.68-6.62z" />
            <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.81.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.02-3.7H.96v2.34A9 9 0 0 0 9 18z" />
            <path fill="#FBBC05" d="M3.98 10.72a5.41 5.41 0 0 1 0-3.44V4.94H.96a9 9 0 0 0 0 8.12l3.02-2.34z" />
            <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 .96 4.94l3.02 2.34C4.68 5.16 6.66 3.58 9 3.58z" />
        </svg>
    );
}

/**
 * @param {object} props
 * @param {(data:object)=>void} props.onSuccess  signed in (cookies set)
 * @param {(message:string)=>void} props.onError
 * @param {boolean} [props.disabled]
 * @param {(code:string)=>void} [props.onCode]  take the raw Google code instead of signing in here (used by /desktop-login)
 * @param {string} [props.label]
 */
export default function GoogleLoginButton({ onSuccess, onError, disabled, onCode, label = 'Sign in with Google' }) {
    const desktop = onCode ? null : desktopGoogleSignIn();
    const [waiting, setWaiting] = useState(false);

    // Shared result handling for /auth/google and /auth/desktop/exchange.
    const finish = useCallback(({ res, data }) => {
        if (res.ok && data.success) onSuccess?.(data);
        else if (res.status === 403) onError?.(data.message || 'Please sign in with your Celebration Chevrolet account.');
        else onError?.(data.message || 'Google sign-in failed. Please try again.');
    }, [onSuccess, onError]);

    const login = useGoogleLogin({
        flow: 'auth-code',
        // UX hint only — pre-filters the account chooser to our domain. The real
        // domain enforcement happens on the backend (payload.hd check).
        hd: 'celebrationchevrolet.com',
        onSuccess: async ({ code }) => {
            if (onCode) return onCode(code);
            try {
                finish(await postJson(`${apiBase}/auth/google`, { code }));
            } catch (err) {
                console.error('Google login error:', err);
                onError?.('Connection error. Please try again.');
            }
            return undefined;
        },
        onError: (err) => {
            console.error('Google login error:', err);
            onError?.('Google sign-in was cancelled or failed.');
        }
    });

    // Desktop: the app hands back { handoff, verifier } (or { error }) after the browser step.
    useEffect(() => {
        if (!desktop) return undefined;
        const unsubscribe = desktop.onGoogleHandoff(async ({ handoff, verifier, error } = {}) => {
            setWaiting(false);
            if (error || !handoff || !verifier) {
                onError?.(error || 'Google sign-in was cancelled or failed.');
                return;
            }
            try {
                finish(await postJson(`${apiBase}/auth/desktop/exchange`, { handoff, verifier }));
            } catch (err) {
                console.error('Desktop Google login error:', err);
                onError?.('Connection error. Please try again.');
            }
        });
        return typeof unsubscribe === 'function' ? unsubscribe : undefined;
    }, [desktop, finish, onError]);

    const start = () => {
        if (!desktop) return login();
        setWaiting(true);
        desktop.startGoogleSignIn();
        return undefined;
    };

    return (
        <>
            <button
                type="button"
                className="btn-google"
                onClick={start}
                disabled={disabled || waiting}
            >
                <GoogleIcon />
                <span>{waiting ? 'Continue in your browser…' : label}</span>
            </button>
            {waiting && (
                <p className="google-waiting-hint">
                    Finish signing in in the browser window that just opened.{' '}
                    <button type="button" className="google-waiting-link" onClick={start}>Open it again</button>
                    {' · '}
                    <button type="button" className="google-waiting-link" onClick={() => setWaiting(false)}>Cancel</button>
                </p>
            )}
        </>
    );
}
